import { ListingStatus, usePauseListingMutation, useResumeListingMutation } from "@/api/listings";
import { listingsQuery } from "@/api/supplier";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { DataTableHeader } from "@/components/data-table/DataTableHeader";
import { Beta } from "@/components/headless/Beta";
import { StyledLink } from "@/components/link/StyledLink";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogClose } from "@/components/ui/Dialog";
import { DropdownMenu, DropdownMenuItem } from "@/components/ui/DropdownMenu";
import { IconButton } from "@/components/ui/IconButton";
import { ButtonLink } from "@/components/ui/Link";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { seo } from "@/utils/seo";
import { Center, QueryHandler, Select, Text } from "@givenwell/components";
import { DotsIcon } from "@givenwell/core-solid-icons";
import { ListingBrief, SupplierListingBrief } from "@givenwell/management-api";
import { colors } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import removeMarkdown from "remove-markdown";
import { fallback, object, optional, picklist } from "valibot";
import { ListingStatusBadge } from "../../../components/listings/ListingStatusBadge";

const LISTING_STATUSES: Record<ListingStatus, string> = {
  closed: "Closed",
  declined: "Declined",
  draft: "Draft",
  pending: "Pending",
  published: "Published",
  paused: "Paused",
};

const LISTING_STATUSES_ARR = [
  "closed",
  "declined",
  "draft",
  "pending",
  "published",
  "paused",
] as const;

export const Route = createFileRoute("/supplier/$supplierId/eap/")({
  component: Page,
  validateSearch: object({
    status: fallback(optional(picklist(LISTING_STATUSES_ARR)), undefined),
  }),
  head: () => ({
    meta: seo({ title: "EAP Listings" }),
  }),
});

function Page() {
  const { supplierId } = Route.useParams();
  const search = Route.useSearch();
  const { status } = search;
  const navigate = useNavigate({ from: "/supplier/$supplierId/eap/" });
  const query = useQuery(listingsQuery(supplierId, status));

  return (
    <QueryHandler
      query={query}
      success={({ data: listings }) => {
        const eapListings = listings.filter((listing) => listing.is_eap);
        return (
          <PageWrapper
            title="EAP Listings"
            maxWidth={1400}
            rightContent={
              <>
                <Beta>
                  <ButtonLink
                    variant="filled"
                    color="primary"
                    to="/supplier/$supplierId/eap/new"
                    params={{
                      supplierId,
                    }}
                  >
                    Add EAP Listing
                  </ButtonLink>
                </Beta>
              </>
            }
          >
            <DataTable>
              <DataTableHeader hideClear hideSearch hideToggle>
                <Select
                  size="sm"
                  css={{
                    w: 150,
                  }}
                  value={status || ""}
                  onValueChange={(v) =>
                    navigate({
                      search: {
                        ...search,
                        status: (v as ListingStatus) || undefined,
                      },
                    })
                  }
                >
                  <option value="">Any Status</option>
                  {Object.entries(LISTING_STATUSES).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ))}
                </Select>
              </DataTableHeader>
              <DataTableBody
                data={eapListings}
                status={query.status}
                dataKey="id"
                rounded="bottom"
                empty={
                  <Center css={{ h: 320, w: "100%", flexDirection: "column", gap: 16 }}>
                    <Text css={{ color: colors.text2, weight: 500 }}>
                      Get started by creating a listing
                    </Text>
                    <ButtonLink
                      variant="filled"
                      color="primary"
                      to="/supplier/$supplierId/eap/new"
                      params={{
                        supplierId,
                      }}
                    >
                      Add Listing
                    </ButtonLink>
                  </Center>
                }
                columns={[
                  {
                    header: "Title",
                    cell: (listing) => (
                      <StyledLink
                        to="/supplier/$supplierId/eap/$listingId/$version"
                        params={{
                          supplierId,
                          listingId: listing.id,
                          version: listing.version,
                        }}
                      >
                        {listing.title}
                      </StyledLink>
                    ),
                  },
                  {
                    header: "Description",
                    cell: (listing) => (
                      <div
                        style={{ maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis" }}
                      >
                        {removeMarkdown(listing.description)}
                      </div>
                    ),
                  },
                  {
                    header: "Status",
                    cell: (listing) => (
                      <ListingStatusBadge
                        status={listing.version_status}
                        version={listing.version}
                      />
                    ),
                  },
                  {
                    columnKey: "actions",
                    header: undefined,
                    css: {
                      textAlign: "right",
                    },
                    cell: (listing) => <ListingDropdownMenu listing={listing} />,
                  },
                ]}
              />
            </DataTable>
          </PageWrapper>
        );
      }}
    />
  );
}

function ListingDropdownMenu({ listing }: { listing: ListingBrief | SupplierListingBrief }) {
  const { supplierId } = Route.useParams();
  const resumeMut = useResumeListingMutation(supplierId);

  const [pauseOpen, setPauseOpen] = useState(false);

  return (
    <>
      <DropdownMenu
        content={
          <>
            {listing.version_status === "paused" ? (
              <DropdownMenuItem
                onSelect={() => {
                  resumeMut.mutate({
                    listingId: listing.id,
                  });
                }}
              >
                Unpause Listing
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onSelect={() => {
                  setTimeout(() => {
                    setPauseOpen(true);
                  }, 0);
                }}
              >
                Pause Listing
              </DropdownMenuItem>
            )}
          </>
        }
      >
        <IconButton aria-label="Listing Actions" size="sm" color="neutral" variant="plain">
          <DotsIcon />
        </IconButton>
      </DropdownMenu>
      <PauseListingDialog open={pauseOpen} onOpenChange={setPauseOpen} listingId={listing.id} />
    </>
  );
}

function PauseListingDialog({
  open,
  onOpenChange,
  listingId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listingId: string;
}) {
  const { supplierId } = Route.useParams();
  const pauseMut = usePauseListingMutation(supplierId);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Pause Listing"
      description="Pausing your listing will hide it from the Givenwell Marketplace, and you will no longer receive new sales."
      footer={
        <>
          <DialogClose>
            <Button variant="tonal" color="primary">
              Back
            </Button>
          </DialogClose>
          <Button
            variant="filled"
            color="primary"
            loading={pauseMut.isPending}
            onClick={() =>
              pauseMut.mutate(
                {
                  listingId: listingId,
                },
                {
                  onSuccess: () => {
                    onOpenChange(false);
                  },
                },
              )
            }
          >
            Pause
          </Button>
        </>
      }
    ></Dialog>
  );
}
